import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from '../services/api'
import toast from 'react-hot-toast'

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,

      // Set auth data
      setAuth: (user, token) => {
        set({ user, token, error: null })
        if (token) {
          localStorage.setItem('token', token)
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
        }
      },

      // Clear auth data
      clearAuth: () => {
        set({ user: null, token: null, error: null })
        localStorage.removeItem('token')
        delete api.defaults.headers.common['Authorization']
      },

      // Register user
      register: async (userData) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/auth/register', userData)
          const { user, token } = response.data
          
          get().setAuth(user, token)
          toast.success('Account created successfully!')
          return { success: true }
        } catch (error) {
          const message = error.response?.data?.message || 'Registration failed'
          set({ error: message })
          toast.error(message)
          return { success: false, error: message }
        } finally {
          set({ isLoading: false })
        }
      },

      // Login user
      login: async (credentials) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/auth/login', credentials)
          const { user, token } = response.data
          
          get().setAuth(user, token)
          toast.success(`Welcome back, ${user.username}!`)
          return { success: true }
        } catch (error) {
          const message = error.response?.data?.message || 'Login failed'
          set({ error: message })
          toast.error(message)
          return { success: false, error: message }
        } finally {
          set({ isLoading: false })
        }
      },

      // Logout user
      logout: async () => {
        set({ isLoading: true })
        try {
          await api.post('/auth/logout')
          toast.success('Logged out successfully')
        } catch (error) {
          console.error('Logout error:', error)
        } finally {
          get().clearAuth()
          set({ isLoading: false })
        }
      },

      // Check authentication status
      checkAuth: async () => {
        const token = localStorage.getItem('token')
        if (!token) {
          set({ isLoading: false })
          return
        }

        set({ isLoading: true })
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
          const response = await api.get('/auth/me')
          const { user } = response.data
          
          set({ user, token, isLoading: false })
        } catch (error) {
          console.error('Auth check failed:', error)
          get().clearAuth()
          set({ isLoading: false })
        }
      },

      // Update user profile
      updateProfile: async (updateData) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.put('/users/profile', updateData)
          const { user } = response.data
          
          set({ user })
          toast.success('Profile updated successfully!')
          return { success: true }
        } catch (error) {
          const message = error.response?.data?.message || 'Update failed'
          set({ error: message })
          toast.error(message)
          return { success: false, error: message }
        } finally {
          set({ isLoading: false })
        }
      },

      // Update user status
      updateStatus: async (status) => {
        try {
          const response = await api.put('/users/status', { status })
          const { user } = response.data
          set({ user })
        } catch (error) {
          console.error('Status update failed:', error)
        }
      },

      // Clear error
      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        token: state.token 
      }),
    }
  )
)